import { logger } from '@/modules/common/logging/logger';
import { SizeShort } from '@/modules/common/types/general';
import { ModalId } from '@/modules/common/components/ModalManager/constants';
import { ToolId } from '@modules/toolbar/types';

import { IAppService } from './IAppService';
import { FloorplanInteractionMode, LoadingType, Module } from '../types';
import { CanvasOverlayRequest } from '../store/useAppStore';

export class AppServiceLogDecorator implements IAppService {
  private decoree: IAppService;

  constructor(decoree: IAppService) {
    this.decoree = decoree;
  }

  //
  // Toolbar
  //

  activateTool(tool: ToolId): void {
    const start = this.logStart(`activateTool (${tool})`);
    this.decoree.activateTool(tool);
    this.logEnd(`activateTool (${tool})`, start);
  }

  resetTool(): void {
    const start = this.logStart('resetTool');
    this.decoree.resetTool();
    this.logEnd('resetTool', start);
  }

  //
  // Module
  //

  getActiveModule(): Module {
    const start = this.logStart('getActiveModule');
    const result = this.decoree.getActiveModule();
    this.logEnd('getActiveModule', start);
    return result;
  }

  goToModule(module: Module): void {
    const start = this.logStart(`goToModule (${module})`);
    this.decoree.goToModule(module);
    this.logEnd(`goToModule (${module})`, start);
  }

  //
  // Floorplan mode
  //

  setFloorplanInteractionMode(mode: FloorplanInteractionMode): void {
    const start = this.logStart('setFloorplanInteractionMode');
    this.decoree.setFloorplanInteractionMode(mode);
    this.logEnd('setFloorplanInteractionMode', start);
  }

  //
  // Selection
  //

  selectAll(): void {
    const start = this.logStart('selectAll');
    this.decoree.selectAll();
    this.logEnd('selectAll', start);
  }

  setSelectedIds(ids: string[]): void {
    const start = this.logStart('setSelectedIds');
    this.decoree.setSelectedIds(ids);
    this.logEnd('setSelectedIds', start);
  }

  resetSelection(): void {
    const start = this.logStart('resetSelection');
    this.decoree.resetSelection();
    this.logEnd('resetSelection', start);
  }

  removeSelectedIds(ids: string[]): void {
    const start = this.logStart('removeSelectedIds');
    this.decoree.removeSelectedIds(ids);
    this.logEnd('removeSelectedIds', start);
  }

  setDisabledElementIds(ids: string[]): void {
    const start = this.logStart('setDisabledElementIds');
    this.decoree.setDisabledElementIds(ids);
    this.logEnd('setDisabledElementIds', start);
  }

  setClickableElementIds(ids: string[]): void {
    const start = this.logStart('setClickableElementIds');
    this.decoree.setClickableElementIds(ids);
    this.logEnd('setClickableElementIds', start);
  }

  //
  // Modal
  //

  public setActiveModalId(id: ModalId | null): void {
    const start = this.logStart(`setActiveModalId --> ${id}`);
    this.decoree.setActiveModalId(id);
    this.logEnd(`setActiveModalId --> ${id}`, start);
  }

  //
  // Popover
  //

  setActivePopoverId(id: null | string): void {
    const start = this.logStart(`setActivePopoverId (${id})`);
    this.decoree.setActivePopoverId(id);
    this.logEnd(`setActivePopoverId (${id})`, start);
  }

  //
  // Canvas overlay
  //

  setCanvasOverlay(request: CanvasOverlayRequest | null): void {
    const start = this.logStart(`setCanvasOverlay (${request === null ? 'clear' : 'show'})`);
    this.decoree.setCanvasOverlay(request);
    this.logEnd(`setCanvasOverlay (${request === null ? 'clear' : 'show'})`, start);
  }

  //
  // Canvas viewport
  //

  setCanvasViewport(size: SizeShort): void {
    const start = this.logStart(`setCanvasViewport (${size.w}x${size.h})`);
    this.decoree.setCanvasViewport(size);
    this.logEnd(`setCanvasViewport (${size.w}x${size.h})`, start);
  }

  //
  // Revision
  //

  async loadRevision(projectId: string, alternativeId: string, revisionId: string): Promise<void> {
    const start = this.logStart(`loadRevision (${projectId}, ${alternativeId}, ${revisionId})`);
    await this.decoree.loadRevision(projectId, alternativeId, revisionId);
    this.logEnd(`loadRevision (${projectId}, ${alternativeId}, ${revisionId})`, start);
  }

  disposeLoadedRevision(): void {
    const start = this.logStart(`disposeLoadedRevision`);
    this.decoree.disposeLoadedRevision();
    this.logEnd(`disposeLoadedRevision`, start);
  }

  startAutosaving(): void {
    const start = this.logStart(`startAutosaving`);
    this.decoree.startAutosaving();
    this.logEnd(`startAutosaving`, start);
  }

  stopAutosaving(): void {
    const start = this.logStart(`stopAutosaving`);
    this.decoree.stopAutosaving();
    this.logEnd(`stopAutosaving`, start);
  }

  async loadVersion(revisionId: string): Promise<void> {
    const start = this.logStart('loadVersion');
    await this.decoree.loadVersion(revisionId);
    this.logEnd('loadVersion', start);
  }

  async finalizeRevision(): Promise<void> {
    const start = this.logStart('finalizeRevision');
    await this.decoree.finalizeRevision();
    this.logEnd('finalizeRevision', start);
  }

  async restoreRevision(): Promise<void> {
    const start = this.logStart('restoreRevision');
    await this.decoree.restoreRevision();
    this.logEnd('restoreRevision', start);
  }

  //
  // Loading container
  //

  showLoadContainer(type: LoadingType): void {
    const start = this.logStart(`showLoadContainer (${type})`);
    this.decoree.showLoadContainer(type);
    this.logEnd(`showLoadContainer (${type})`, start);
  }

  hideLoadContainer(): void {
    const start = this.logStart(`hideLoadContainer`);
    this.decoree.hideLoadContainer();
    this.logEnd(`hideLoadContainer`, start);
  }

  // private utility methods

  private logStart(methodName: string): number {
    const start: number = performance.now();
    logger.info(`Start -> ${methodName}`);
    return start;
  }

  private logEnd(methodName: string, start: number): void {
    logger.info(`End -> ${methodName} (${(performance.now() - start).toFixed(2)}ms)`);
  }
}
